import './Request.css';
import { useEffect, useState } from 'react';
import { Divider, Grid, Typography, GridWrap } from '@mui/material';
import { t } from 'i18next';
import { useRequestContext } from '../Request.Context';
import { useAppSelector } from '../../../shared/redux/store';
import { useRequestdata } from '../useRequestData';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { equivalencyRequestNavigation } from '../../App.routes';
import { fadingClassName } from '../../../shared/utils/commons';
import AcademicHistoryStep from './RequestSteps/AcademicHistoryStep/AcademicHistoryStep';
import StudentDataStep from './RequestSteps/StudentDataStep/StudentDataStep';
import InitialStep from './RequestSteps/InitialStep/InitialStep';
import RequestStepper from './RequestStepper/RequestStepper';
import FinalStep from './RequestSteps/FinalStep/FinalStep';
import useDebounce from '../../../shared/hooks/useDebounce';
import STEPS, { StepList } from './STEPS';
import AuthSuggestionModal from '../../../components/Modals/AuthSuggestionModal';
import TargetSelectionStep from './RequestSteps/TargetSelectionStep/TargetSelectionStep';
import ConsultRequestStep from '../../ConsultRequest/ConsultRequestStep';
import DocumentUploadStep from './RequestSteps/DocumentationDataStep/DocumentationDataStep';

const Request: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [debouncedActiveStep, isSteppingOut] = useDebounce(activeStep, 100);
  const { handleBackStep, handleNextStep } = useRequestContext();
  const navigate = useNavigate();
  const location = useLocation();
  let hiddenNavbar = false;
  const [searchUrlParams] = useSearchParams();
  const embeded = searchUrlParams.get('embeded');
  if (embeded && embeded === 'true') {
    hiddenNavbar = true;
  }

  const {
    allReadySaved
  } = useRequestdata({});

  const welcomeScreen = useAppSelector(state => state.settings.welcomeScreen);

  const resetStep = () => {
    setActiveStep(0);
    setShowWelcomeScreen(true)
  }

  const redirectTo = (querySearch: string) => {
    navigate(equivalencyRequestNavigation.searchEquivalency(querySearch));
  };

  const stepperSteps: StepList[] = [
    {
      key: STEPS.STUDENT_DATA,
      title: t('titleStudentDataStep'),
      component: <StudentDataStep />
    },
    {
      key: STEPS.SIMULATION,
      title: t('equivalencyExploreTitle'),
      component: <ConsultRequestStep />
    },
    {
      key: STEPS.ACADEMIC_HISTORY,
      title: t('titleAcademicHistoryStep'), 
      component: <AcademicHistoryStep />
    },
    {
      key: STEPS.TARGET_SELECTION,
      title: t('titleTargetSelectionStep'),
      component: <TargetSelectionStep />
    },
    {
    key: STEPS.DOCUMENT_UPLOAD, 
    title: t('titleUniversityDocumentsStep'),
    component: <DocumentUploadStep requestData={useRequestdata({})} />
    }
  ];

  const viewSteps = [
    ...stepperSteps,
    {
      key: STEPS.FINISH,
      title: t('titleFinalStep'),
      component: <FinalStep
        redirectTo={redirectTo}
        onReset={resetStep}
        requestData={useRequestdata({})}
      />
    }
  ];

  const beginRequest = () => setShowWelcomeScreen(false);

  useEffect(() => {
    if (location?.state?.fromConsult) {
      setShowWelcomeScreen(false);
      location.state.fromConsult = '';
    } else {
      setShowWelcomeScreen(welcomeScreen);
    }
  }, [welcomeScreen]);

  return (
    <>
      <AuthSuggestionModal/>
      {
        showWelcomeScreen
          ? <InitialStep beginRequest={beginRequest} />
          : <Grid container
            direction='column'
            justifyContent="space-between"
            alignItems="center"
            className='REQXXI-Stepper-Container fadeIn'
          >
            <Grid item className='REQXXI-Stepper-Header'>
              <Grid container
                justifyContent='center'
                alignContent="center"
                style={{ height: '100%', display: 'flex' }}
              >
                <Typography variant="h5" className={`REQXXI-Stepper-Header-Title ${fadingClassName(isSteppingOut)}`}>{viewSteps[debouncedActiveStep].title}</Typography>
              </Grid>
            </Grid>
            <Grid tabIndex={-1} item className={hiddenNavbar ? 'REQXXI-Stepper-Content-HiddenNavbar' : 'REQXXI-Stepper-Content'}>
              <div key={debouncedActiveStep} className={`REQXXI-Stepper-Content-Div ${fadingClassName(isSteppingOut)}`}>
                {viewSteps[debouncedActiveStep].component}
              </div>
            </Grid>
            {activeStep !== stepperSteps.length &&
              <Grid
                item
                wrap={'no-wrap' as GridWrap}
                className='REQXXI-Stepper-Footer'
                alignContent={'center'}
                direction='column'
                height={'100%'}
              >
                <Divider />
                <RequestStepper
                  steps={stepperSteps}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  onNext={handleNextStep}
                  onBack={handleBackStep}
                  disbaleGoBack={allReadySaved}
                  showWelcomeScreen={() => setShowWelcomeScreen(true)}
                />
              </Grid>
            }
          </Grid>}
    </>);
};

export default Request;
